import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Image, Loader2, X, Upload, Check, EyeOff, Car, Gauge, Palette, Calendar, Hash, FileText, Settings, Star, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Copy, ArrowUpDown, ArrowUp, ArrowDown, Package, DollarSign, Clock, Ban, Sparkles, Smartphone, QrCode, List, LayoutGrid, Printer, Facebook, Zap, Film } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import WindowSticker from '../../components/WindowSticker';
import BulkWindowSticker from '../../components/BulkWindowSticker';
import { VideoReviewDialog } from '../../components/admin/VideoReviewDialog';
import { VideoStyleDialog } from '../../components/admin/VideoStyleDialog';
import { VideoStylesManager } from '../../components/admin/VideoStylesManager';
import { PriceChangedBadge } from '../../components/admin/PriceChangedBadge';
import { CarfaxSyncButton } from '../../components/admin/CarfaxSyncButton';
import { CarfaxMissingButton } from '../../components/admin/CarfaxMissingButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URL - handles relative paths
const normalizeImageUrl = (url) => {
  if (!url) return null;
  // Convert relative paths to full URLs
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  // Ensure URL is properly encoded/decoded
  try {
    // If URL contains encoded characters, it's already encoded
    if (url.includes('%')) {
      return url;
    }
    // Otherwise encode it
    return encodeURI(url);
  } catch {
    return url;
  }
};

// Fallback placeholder for broken images
const FALLBACK_PLACEHOLDER = "https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460688/entrepot/placeholders/new_arrival_placeholder.webp";

// Available equipment options
const EQUIPMENT_OPTIONS = [
  // Comfort
  'Climatisation', 'Climatisation automatique', 'Sièges chauffants', 'Sièges ventilés', 'Sièges en cuir',
  'Sièges électriques', 'Volant chauffant', 'Toit ouvrant', 'Toit panoramique',
  // Technology
  'Navigation GPS', 'Bluetooth', 'Apple CarPlay', 'Android Auto', 'Écran tactile',
  'Caméra de recul', 'Caméra 360°', 'Capteurs de stationnement', 'Régulateur de vitesse',
  'Régulateur de vitesse adaptatif', 'Aide au stationnement',
  // Safety
  'Alerte de collision', 'Freinage d\'urgence automatique', 'Surveillance des angles morts',
  'Alerte de sortie de voie', 'Phares automatiques', 'Phares LED', 'Phares adaptatifs',
  // Audio
  'Système audio premium', 'Radio satellite', 'Prise USB', 'Chargeur sans fil',
  // Exterior
  'Jantes en alliage', 'Barres de toit', 'Marchepieds', 'Attelage de remorque',
  // Other
  'Démarrage sans clé', 'Démarrage à distance', 'Hayon électrique', 'Portes coulissantes électriques',
  'Mémoire des sièges', 'Rétroviseurs chauffants', 'Essuie-glaces automatiques',
];

// Auto-select likely-standard equipment based on VMR decoded data (year/trim/body/fuel).
// Preserves anything the user has already checked.
const autoSelectFeatures = (data, existing = []) => {
  const picked = new Set(existing);
  const year = parseInt(data.year, 10) || 0;
  const trim = (data.trim || '').toLowerCase();
  const body = (data.body_type || '').toLowerCase();
  const fuel = (data.fuel_type || '').toLowerCase();
  const drive = (data.drivetrain || '').toLowerCase();

  const add = (f) => { if (EQUIPMENT_OPTIONS.includes(f)) picked.add(f); };

  // Year-based (regulated / mass-market standards)
  if (year >= 2010) { add('Climatisation'); add('Régulateur de vitesse'); add('Prise USB'); }
  if (year >= 2012) { add('Bluetooth'); add('Phares automatiques'); }
  if (year >= 2016) { add('Écran tactile'); add('Apple CarPlay'); add('Android Auto'); }
  if (year >= 2018) { add('Caméra de recul'); add('Alerte de collision'); add('Freinage d\'urgence automatique'); }
  if (year >= 2019) { add('Phares LED'); add('Surveillance des angles morts'); add('Alerte de sortie de voie'); }
  if (year >= 2021) { add('Régulateur de vitesse adaptatif'); add('Démarrage sans clé'); }

  // Trim-based (luxury / premium packages)
  const luxuryTrims = ['limited', 'premium', 'platinum', 'touring', 'xle', 'ltz', 'luxury', 'laramie', 'sel', 'sport', 'exl', 'sr5', 'denali', 'reserve'];
  if (luxuryTrims.some(l => trim.includes(l))) {
    add('Sièges chauffants'); add('Sièges en cuir'); add('Sièges électriques');
    add('Volant chauffant'); add('Navigation GPS'); add('Système audio premium');
    add('Toit ouvrant'); add('Jantes en alliage'); add('Démarrage à distance');
    add('Mémoire des sièges');
  }

  // Body-based
  if (body === 'suv' || body === 'truck' || body === 'van') {
    add('Barres de toit'); add('Attelage de remorque');
  }
  if (body === 'suv' && year >= 2015) add('Hayon électrique');

  // Fuel-based
  if (fuel === 'hybrid' || fuel === 'electric') add('Démarrage à distance');

  // Drivetrain already shown in specs — no feature mapping needed

  // Always-present comfort basics on any 2010+ vehicle
  if (year >= 2010) {
    add('Rétroviseurs chauffants'); add('Essuie-glaces automatiques');
  }

  return Array.from(picked);
};

const initialFormState = {
  make: '',
  model: '',
  year: new Date().getFullYear(),
  price: '',
  regular_price: '',
  mileage: '',
  body_type: 'sedan',
  transmission: 'automatic',
  fuel_type: 'gasoline',
  drivetrain: 'fwd',
  exterior_color: '',
  interior_color: '',
  vin: '',
  stock_number: '',
  trim: '',
  title: '',
  engine: '',
  seats: '',
  description: '',
  features: [],
  images: [],
  status: 'available',
  is_featured: false,
  carfax_accident_free: false,
  carfax_one_owner: false,
  video_url: '',
  video_style: 'coastal',
};

// SectionHeader component for collapsible form sections
const SectionHeader = ({ id, icon: Icon, title, subtitle, children, expanded, onToggle }) => (
  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div className="text-left">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {expanded ? (
        <ChevronUp className="h-5 w-5 text-slate-400" />
      ) : (
        <ChevronDown className="h-5 w-5 text-slate-400" />
      )}
    </button>
    {expanded && (
      <div className="p-4 pt-0 border-t border-slate-100">
        {children}
      </div>
    )}
  </div>
);

// Helper function to get approximate color code from color name
const getColorCode = (colorName) => {
  if (!colorName) return '#e2e8f0';
  const name = colorName.toLowerCase();
  const colorMap = {
    'noir': '#1a1a1a',
    'black': '#1a1a1a',
    'blanc': '#ffffff',
    'white': '#ffffff',
    'argent': '#c0c0c0',
    'silver': '#c0c0c0',
    'gris': '#808080',
    'gray': '#808080',
    'grey': '#808080',
    'rouge': '#dc2626',
    'red': '#dc2626',
    'bleu': '#2563eb',
    'blue': '#2563eb',
    'vert': '#16a34a',
    'green': '#16a34a',
    'jaune': '#eab308',
    'yellow': '#eab308',
    'orange': '#ea580c',
    'brun': '#78350f',
    'brown': '#78350f',
    'beige': '#d4b896',
    'or': '#d4af37',
    'gold': '#d4af37',
    'bronze': '#cd7f32',
    'bordeaux': '#722f37',
    'maroon': '#722f37',
  };
  
  for (const [key, value] of Object.entries(colorMap)) {
    if (name.includes(key)) return value;
  }
  return '#e2e8f0';
};

const AdminVehicles = () => {
  const { getAuthHeader, isAdmin, isViewer } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'card'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [decodingVin, setDecodingVin] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    specs: true,
    colors: true,
    images: true,
    equipment: false,
    status: true,
  });
  const [editingCell, setEditingCell] = useState(null); // { vehicleId, field }
  const [editValue, setEditValue] = useState('');
  const [stickerVehicle, setStickerVehicle] = useState(null); // For window sticker
  const [selectedVehicles, setSelectedVehicles] = useState([]); // For bulk sticker print
  const [review, setReview] = useState(null); // {vehicleId?} when video review dialog is open
  const pendingVideoCount = vehicles.filter(v => v.video_generation_status === 'pending_review').length;
  const [videoStyles, setVideoStyles] = useState([]);
  const [styleRequest, setStyleRequest] = useState(null); // {title, count, allowKeep, defaultStyle, run(style)}
  const [stylesManagerOpen, setStylesManagerOpen] = useState(false);
  const [showBulkSticker, setShowBulkSticker] = useState(false); // Bulk sticker modal
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]); // Default to all vehicles
  const [ageFilter, setAgeFilter] = useState(null); // null = all, '0-45', '46-89', '90+'
  const [showKpiCards, setShowKpiCards] = useState(true); // Collapse/expand KPI section
  const fileInputRef = useRef(null);
  const descriptionTimeoutRef = useRef(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  // Auto-refresh the vehicle list every 30 s while any card is generating a PixVerse
  // video. Stops as soon as none are in 'queued' or 'generating', so no polling cost
  // when the lot is idle.
  useEffect(() => {
    const anyPending = vehicles.some(v =>
      v.video_generation_status === 'queued' || v.video_generation_status === 'generating'
    );
    if (!anyPending) return;
    const id = setInterval(() => fetchVehicles(), 30000);
    return () => clearInterval(id);
  }, [vehicles]);

  // Auto-generate description when relevant fields change
  useEffect(() => {
    // Only auto-generate if dialog is open, auto-generate is enabled, and we have basic info
    if (!dialogOpen || !autoGenerateEnabled || !form.make || !form.model || !form.year) {
      return;
    }
    
    // Clear previous timeout
    if (descriptionTimeoutRef.current) {
      clearTimeout(descriptionTimeoutRef.current);
    }
    
    // Debounce: wait 1.5 seconds after last change before generating
    descriptionTimeoutRef.current = setTimeout(() => {
      // Only generate if we have at least one feature, CARFAX badge, or title checked
      const hasContent = (form.features && form.features.length > 0) || 
                         form.title;
      
      if (hasContent && !generatingDescription) {
        generateDescriptionSilent();
      }
    }, 1500);
    
    return () => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, [form.features, form.title, form.trim, dialogOpen, autoGenerateEnabled]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?limit=500&include_offline=true&_t=${Date.now()}`, {
        headers: getAuthHeader(),
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  // Generate AI description (silent - no toast on success)
  const generateDescriptionSilent = async () => {
    if (!form.make || !form.model || !form.year || generatingDescription) {
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const response = await axios.post(`${API}/vehicles/generate-description`, {
        make: form.make,
        model: form.model,
        year: parseInt(form.year),
        trim: form.trim || null,
        title: form.title || null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        transmission: form.transmission || null,
        drivetrain: form.drivetrain || null,
        fuel_type: form.fuel_type || null,
        exterior_color: form.exterior_color || null,
        features: form.features || [],
        carfax_accident_free: (form.carfax_badges || []).includes('AccidentFree'),
        carfax_one_owner: (form.carfax_badges || []).includes('OneOwner'),
      }, {
        headers: getAuthHeader(),
      });
      
      setForm(prev => ({ ...prev, description: response.data.description }));
    } catch (error) {
      console.error('Error generating description:', error);
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Generate AI description (with toast feedback)
  const generateDescription = async () => {
    if (!form.make || !form.model || !form.year) {
      toast.error('Veuillez d\'abord remplir la marque, le modèle et l\'année');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const response = await axios.post(`${API}/vehicles/generate-description`, {
        make: form.make,
        model: form.model,
        year: parseInt(form.year),
        trim: form.trim || null,
        title: form.title || null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        transmission: form.transmission || null,
        drivetrain: form.drivetrain || null,
        fuel_type: form.fuel_type || null,
        exterior_color: form.exterior_color || null,
        features: form.features || [],
        carfax_accident_free: (form.carfax_badges || []).includes('AccidentFree'),
        carfax_one_owner: (form.carfax_badges || []).includes('OneOwner'),
      }, {
        headers: getAuthHeader(),
      });
      
      setForm(prev => ({ ...prev, description: response.data.description }));
      toast.success('Description générée avec succès!');
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Erreur lors de la génération de la description');
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted and filtered vehicles
  const getSortedVehicles = () => {
    // First filter by status
    // Treat 'new_arrival' as 'available' for filtering purposes
    let filtered = vehicles.filter(v => {
      if (statusFilter.length === 0) return true;
      const effectiveStatus = v.status === 'new_arrival' ? 'available' : v.status;
      return statusFilter.includes(effectiveStatus);
    });
    
    // Filter by age bracket
    if (ageFilter) {
      filtered = filtered.filter(v => {
        const age = calculateInventoryAge(v);
        if (age === '—') return ageFilter === '90+'; // No date = treat as old
        if (ageFilter === '0-45') return age >= 0 && age <= 45;
        if (ageFilter === '46-89') return age >= 46 && age <= 89;
        if (ageFilter === '90+') return age >= 90;
        return true;
      });
    }
    
    // Then filter by search
    filtered = filtered.filter(v => 
      `${v.make} ${v.model} ${v.year} ${v.vin || ''} ${v.exterior_color || ''} ${v.stock_number || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'images') {
          aVal = a.images?.length || 0;
          bVal = b.images?.length || 0;
        } else if (sortConfig.key === 'age' || sortConfig.key === 'inventory_age') {
          // Sort by inventory_date (Row A from sheet) if available, else by created_at
          const aDate = a.inventory_date || a.created_at;
          const bDate = b.inventory_date || b.created_at;
          aVal = aDate ? new Date(aDate).getTime() : 0;
          bVal = bDate ? new Date(bDate).getTime() : 0;
        } else if (sortConfig.key === 'vehicle') {
          aVal = `${a.make} ${a.model}`.toLowerCase();
          bVal = `${b.make} ${b.model}`.toLowerCase();
        }

        // Handle null/undefined
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        // Compare
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('fr-CA').format(km);
  };

  // Calculate how long the vehicle has been in inventory based on inventory_date (Row A from sheet)
  const calculateInventoryAge = (vehicle) => {
    // Use inventory_date (from Row A - purchase date) if available
    const dateToUse = vehicle.inventory_date || vehicle.created_at;
    if (!dateToUse) return '—';
    
    const created = new Date(dateToUse);
    const now = new Date();
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // KPI calculations - treat 'new_arrival' as 'available'
  // Active vehicles = those that are available, new_arrival, or reserved (not sold/offline)
  const activeVehicles = vehicles.filter(v => 
    v.status === 'available' || v.status === 'new_arrival' || v.status === 'reserved'
  );
  
  // Age bracket calculations for active vehicles
  const getAgeStats = (minAge, maxAge = Infinity) => {
    const inBracket = activeVehicles.filter(v => {
      const age = calculateInventoryAge(v);
      if (age === '—') return maxAge === Infinity; // No date = oldest bracket
      return age >= minAge && age <= maxAge;
    });
    
    const totalCost = inBracket.reduce((sum, v) => sum + (v.cost || 0), 0);
    const totalProfit = inBracket.reduce((sum, v) => sum + (v.profit || 0), 0);
    const avgCost = inBracket.length > 0 ? totalCost / inBracket.length : 0;
    
    return {
      count: inBracket.length,
      percentage: activeVehicles.length > 0 ? Math.round((inBracket.length / activeVehicles.length) * 100) : 0,
      totalCost,
      avgCost,
      totalProfit
    };
  };
  
  const age0to45 = getAgeStats(0, 45);
  const age46to89 = getAgeStats(46, 89);
  const age90plus = getAgeStats(90, Infinity);
  
  // Calculate average age
  const calculateAvgAge = () => {
    const ages = activeVehicles.map(v => calculateInventoryAge(v)).filter(a => a !== '—');
    if (ages.length === 0) return 0;
    return Math.round(ages.reduce((sum, a) => sum + a, 0) / ages.length);
  };

  const kpiStats = {
    total: vehicles.length,
    available: vehicles.filter(v => v.status === 'available' || v.status === 'new_arrival').length,
    reserved: vehicles.filter(v => v.status === 'reserved').length,
    sold: vehicles.filter(v => v.status === 'sold').length,
    offline: vehicles.filter(v => v.status === 'offline').length,
    totalValue: activeVehicles.reduce((sum, v) => sum + (v.price || 0), 0),
    totalCost: activeVehicles.reduce((sum, v) => sum + (v.cost || 0), 0),
    avgCost: activeVehicles.length > 0 ? activeVehicles.reduce((sum, v) => sum + (v.cost || 0), 0) / activeVehicles.length : 0,
    avgAge: calculateAvgAge(),
    activeCount: activeVehicles.length,
  };

  const handleDecodeVin = async () => {
    if (!form.vin || form.vin.length !== 17) {
      toast.error('Please enter a valid 17-character VIN');
      return;
    }

    setDecodingVin(true);
    try {
      const response = await axios.get(`${API}/vin/decode/${form.vin}`);
      const data = response.data;

      if (data.error) {
        toast.error(`VIN decode error: ${data.error}`);
        return;
      }

      setForm(prev => ({
        ...prev,
        make: data.make || prev.make,
        model: data.model || prev.model,
        year: data.year || prev.year,
        trim: data.trim || prev.trim,
        body_type: data.body_type || prev.body_type,
        transmission: data.transmission || prev.transmission,
        drivetrain: data.drivetrain || prev.drivetrain,
        fuel_type: data.fuel_type || prev.fuel_type,
        engine: data.engine || prev.engine,
        // NOTE: automatic equipment selection intentionally DISABLED — admins pick features manually.
      }));

      toast.success('VIN decoded successfully!');
    } catch (error) {
      toast.error('Failed to decode VIN');
    } finally {
      setDecodingVin(false);
    }
  };


  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/video?vehicle_id=${selectedVehicle?.id || ''}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setForm(prev => ({ ...prev, video_url: res.data.url }));
      toast.success('Vidéo téléversée avec succès');
    } catch (err) {
      toast.error('Erreur lors du téléversement de la vidéo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const formData = new FormData();
    
    // Limit to 10 files at a time for reliability
    const filesToUpload = Array.from(files).slice(0, 10);
    if (files.length > 10) {
      toast.warning(`Téléversement limité à 10 images à la fois. ${files.length - 10} image(s) ignorée(s).`);
    }
    
    for (const file of filesToUpload) {
      formData.append('files', file);
    }

    try {
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: getAuthHeader(),
        timeout: 180000, // 3 minute timeout for large uploads
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });

      if (!response.data.uploaded || response.data.uploaded.length === 0) {
        toast.error('Aucune image téléversée. Vérifiez le format des fichiers.');
        return;
      }

      // Store relative URLs to make them portable across environments
      const newUrls = response.data.uploaded.map(img => img.url);
      
      // Small delay to ensure files are fully written to disk
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update form with new images
      const updatedImages = (() => {
        // Filter out placeholder images when adding new studio photos
        const existingNonPlaceholders = form.images.filter(img => 
          !img.includes('placeholder') && !img.includes('new-arrival') && !img.includes('customer-assets')
        );
        return [...existingNonPlaceholders, ...newUrls];
      })();
      
      setForm(prev => ({
        ...prev,
        images: updatedImages,
      }));

      // AUTO-SAVE: If editing an existing vehicle, save images immediately to database
      if (selectedVehicle) {
        try {
          await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
            images: updatedImages,
          }, {
            headers: getAuthHeader(),
          });
          toast.success(`${response.data.count} image(s) téléversée(s) et sauvegardée(s)!`);
        } catch (saveError) {
          console.error('Auto-save failed:', saveError);
          toast.warning(`Images téléversées mais non sauvegardées. Cliquez sur "Sauvegarder" pour confirmer.`);
        }
      } else {
        toast.success(`${response.data.count} image(s) téléversée(s)! N'oubliez pas de sauvegarder.`);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.error(`Erreurs: ${response.data.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Téléversement trop long. Essayez avec moins d\'images ou des fichiers plus petits.');
      } else if (error.response?.status === 413) {
        toast.error('Fichiers trop volumineux. Réduisez la taille ou le nombre d\'images.');
      } else {
        toast.error('Échec du téléversement. Vérifiez votre connexion et réessayez.');
      }
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = async (index) => {
    const newImages = form.images.filter((_, i) => i !== index);
    setForm(prev => ({
      ...prev,
      images: newImages,
    }));
    
    // Auto-save when editing existing vehicle
    if (selectedVehicle) {
      try {
        await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
          images: newImages,
        }, {
          headers: getAuthHeader(),
        });
        toast.success('Image supprimée et sauvegardée');
      } catch (error) {
        console.error('Failed to auto-save after removing image:', error);
        toast.warning('Image supprimée localement. N\'oubliez pas de sauvegarder.');
      }
    }
  };

  const moveImage = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= form.images.length) return;
    
    const newImages = [...form.images];
    const [moved] = newImages.splice(fromIdx, 1);
    newImages.splice(toIdx, 0, moved);
    
    setForm(prev => ({ ...prev, images: newImages }));
    
    // Auto-save reorder when editing existing vehicle
    if (selectedVehicle) {
      try {
        await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
          images: newImages,
        }, {
          headers: getAuthHeader(),
        });
      } catch (error) {
        console.error('Failed to auto-save image order:', error);
      }
    }
  };

  const [draggedImageIdx, setDraggedImageIdx] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const handleImageDragStart = (e, idx) => {
    setDraggedImageIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleImageDrop = (e, dropIdx) => {
    e.preventDefault();
    if (draggedImageIdx === null || draggedImageIdx === dropIdx) {
      setDraggedImageIdx(null);
      return;
    }
    moveImage(draggedImageIdx, dropIdx);
    setDraggedImageIdx(null);
  };

  // Drag and drop file upload handlers
  const handleFileDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingFiles(true);
    }
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDraggingFiles(false);
    }
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    // Filter for image files only
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (imageFiles.length === 0) {
      toast.error('Veuillez déposer uniquement des fichiers image (PNG, JPG, etc.)');
      return;
    }
    
    // Create a synthetic event to reuse existing upload handler
    const dataTransfer = new DataTransfer();
    imageFiles.forEach(file => dataTransfer.items.add(file));
    
    // Trigger the existing upload handler
    if (fileInputRef.current) {
      fileInputRef.current.files = dataTransfer.files;
      handleImageUpload({ target: { files: dataTransfer.files } });
    }
  };

  const toggleFeature = (feature) => {
    setForm(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleOpenCreate = () => {
    setSelectedVehicle(null);
    setForm(initialFormState);
    setDialogOpen(true);
  };

  const handleOpenEdit = (vehicle) => {
    setSelectedVehicle(vehicle);
    setForm({
      ...initialFormState,
      ...vehicle,
      features: vehicle.features || [],
      images: vehicle.images || [],
      seats: vehicle.seats || '',
      video_style: vehicle.video_style || 'coastal',
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...form,
      year: parseInt(form.year),
      price: parseFloat(form.price),
      regular_price: form.regular_price ? parseFloat(form.regular_price) : null,
      mileage: parseInt(form.mileage),
      seats: form.seats ? parseInt(form.seats) : null,
    };

    try {
      if (selectedVehicle) {
        await axios.put(`${API}/vehicles/${selectedVehicle.id}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success('Vehicle updated successfully');
      } else {
        await axios.post(`${API}/vehicles`, payload, {
          headers: getAuthHeader(),
        });
        toast.success('Vehicle added successfully');
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/vehicles/${selectedVehicle.id}`, {
        headers: getAuthHeader(),
      });
      toast.success('Vehicle deleted successfully');
      setDeleteDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  // Copy VIN to clipboard
  const copyVin = async (vin) => {
    if (!vin) return;
    try {
      await navigator.clipboard.writeText(vin);
      toast.success('VIN copié');
    } catch (err) {
      toast.error('Erreur de copie');
    }
  };

  // Post single vehicle to Facebook Marketplace
  const postToFacebook = (vehicle) => {
    const vehicleData = {
      stock: vehicle.stock_number,
      source: window.location.origin,
      account_id: 'default',
      account_name: 'Default',
      profile_directory: 'Default'
    };
    
    const encoded = encodeURIComponent(JSON.stringify(vehicleData));
    const fbUrl = `https://www.facebook.com/marketplace/create/vehicle?entrepot_vehicle=${encoded}`;
    
    console.log('Opening FB with vehicle:', vehicle.stock_number);
    console.log('URL:', fbUrl);
    
    window.open(fbUrl, '_blank');
    toast.success(`Ouverture FB pour ${vehicle.year} ${vehicle.make} ${vehicle.model}`);
  };

  // Inline editing functions
  const startEditing = (vehicleId, field, currentValue) => {
    // Only admins can edit
    if (isViewer()) return;
    setEditingCell({ vehicleId, field });
    setEditValue(currentValue?.toString() || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (vehicleId, field) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;

    let value = editValue;
    if (field === 'price' || field === 'mileage' || field === 'regular_price') {
      value = parseFloat(editValue) || 0;
      if (field === 'regular_price' && (!editValue || editValue === '0')) {
        value = null;
      }
    }

    // Don't save if value hasn't changed
    if (vehicle[field]?.toString() === editValue) {
      cancelEditing();
      return;
    }

    try {
      await axios.put(`${API}/vehicles/${vehicleId}`, {
        ...vehicle,
        [field]: value,
      }, {
        headers: getAuthHeader(),
      });
      
      // Update local state
      setVehicles(prev => prev.map(v => 
        v.id === vehicleId ? { ...v, [field]: value } : v
      ));
      toast.success('Mis à jour');
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
    cancelEditing();
  };

  const handleKeyDown = (e, vehicleId, field) => {
    if (e.key === 'Enter') {
      saveInlineEdit(vehicleId, field);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Bulk selection functions for sticker printing
  const toggleVehicleSelection = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedVehicles.length === filteredVehicles.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(filteredVehicles.map(v => v.id));
    }
  };

  const getSelectedVehiclesData = () => {
    return vehicles.filter(v => selectedVehicles.includes(v.id));
  };

  const handleBulkPrint = () => {
    if (selectedVehicles.length === 0) {
      toast.warning('Cochez les véhicules à imprimer (case à gauche de chaque ligne, ou la case du haut pour tout sélectionner)');
      return;
    }
    setShowBulkSticker(true);
  };

  const clearSelection = () => {
    setSelectedVehicles([]);
  };

  const priceChangedVehicles = vehicles.filter(v => v.sticker_price_changed);

  const reprintPriceChanged = () => {
    setSelectedVehicles(priceChangedVehicles.map(v => v.id));
    setShowBulkSticker(true);
  };

  // Remember the printed price so the "Prix changé" flag clears
  const markStickersPrinted = async (ids) => {
    try {
      await axios.post(`${API}/admin/vehicles/stickers/printed`, { vehicle_ids: ids }, { headers: getAuthHeader() });
      fetchVehicles();
    } catch (e) { /* non-blocking: printing already happened */ }
  };

  // Regenerate PixVerse videos (ids=null → all AI videos). Hand-uploaded videos are kept.
  const replaceVideos = (ids) => {
    const n = ids ? ids.length : vehicles.filter(v => v.status === 'available' && v.video_url).length;
    setStyleRequest({
      title: ids ? `Régénérer ${n} vidéo(s)` : 'Remplacer toutes les vidéos IA',
      count: n,
      allowKeep: true,
      run: (style) => runReplaceVideos(ids, style),
    });
  };

  const regenerateOne = (vehicle) => {
    setStyleRequest({
      title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      count: 1,
      allowKeep: false,
      defaultStyle: vehicle.video_style || 'coastal',
      run: async (style) => {
        try {
          await axios.post(`${API}/admin/vehicles/${vehicle.id}/regenerate-video`, {}, { headers: getAuthHeader(), params: { style } });
          toast.success('Nouvelle vidéo en génération — elle apparaîtra dans « Vidéos à approuver » d\'ici 2-5 min');
          fetchVehicles();
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Échec de la relance');
        }
      },
    });
  };

  const fetchVideoStyles = async () => {
    try {
      const res = await axios.get(`${API}/admin/video-styles`, { headers: getAuthHeader() });
      setVideoStyles(res.data);
    } catch (e) { /* non-admin or offline — picker just shows nothing */ }
  };

  useEffect(() => { fetchVideoStyles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SMS alert links: /admin/vehicles?review=all|<vehicleId> open the approval queue
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('review');
    if (!target || !isAdmin()) return;
    setReview(target === 'all' ? {} : { vehicleId: target });
    params.delete('review');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runReplaceVideos = async (ids, style) => {
    const toastId = toast.loading('Mise en file des nouvelles vidéos…');
    try {
      const res = await axios.post(`${API}/admin/vehicles/replace-videos`, { vehicle_ids: ids, style }, { headers: getAuthHeader(), timeout: 120000 });
      const d = res.data;
      const skipped = d.skipped_manual + d.skipped_status + d.skipped_busy + (d.skipped_no_photo || 0);
      toast.success(
        `${d.queued} vidéo(s) en régénération — à approuver quand prêtes.` +
        (skipped ? ` Ignorés: ${d.skipped_manual} manuelle(s), ${d.skipped_status} non disponible(s), ${d.skipped_busy} déjà en cours, ${d.skipped_no_photo || 0} sans vraie photo.` : ''),
        { id: toastId, duration: 9000 }
      );
      if (ids) setSelectedVehicles([]);
      fetchVehicles();
    } catch (e) {
      toast.error(`Échec: ${e?.response?.data?.detail || e.message}`, { id: toastId });
    }
  };

  const filteredVehicles = getSortedVehicles();

  const statusColors = {
    available: 'bg-green-100 border-green-200',
    new_arrival: 'bg-blue-100 border-blue-200',
    sold: 'bg-red-100 border-red-200',
    pending: 'bg-yellow-100 border-yellow-200',
    reserved: 'bg-purple-100 border-purple-200',
    offline: 'bg-slate-100 border-slate-200',
  };

  // Explicit text colors for status dropdown (to override dark theme)
  const statusTextColors = {
    available: '#15803d', // green-700
    new_arrival: '#1d4ed8', // blue-700
    sold: '#b91c1c',      // red-700
    pending: '#a16207',   // yellow-700
    reserved: '#7e22ce',  // purple-700
    offline: '#475569',   // slate-600
  };

  const statusLabels = {
    available: 'Disponible',
    new_arrival: 'Nouvel arrivage',
    sold: 'Vendu',
    pending: 'En attente',
    reserved: 'Réservé',
    offline: 'Hors-Ligne',
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Sort indicator helper
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 text-slate-300" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 text-blue-600" />
      : <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-vehicles">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Inventaire des véhicules</h1>
            <p className="text-slate-500 text-sm">{vehicles.length} véhicule(s) au total</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin() && (
              <a 
                href="/upload" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
                title="Ouvrir l'app mobile pour prendre des photos"
              >
                <Smartphone className="h-4 w-4" />
                <span className="hidden sm:inline">App Photos Mobile</span>
                <span className="sm:hidden">Photos</span>
              </a>
            )}
            {isAdmin() && (
              <Button
                onClick={async () => {
                  if (!window.confirm("⚠️ EFFACER l'équipement sur TOUS les véhicules?\n\nCette action vide la liste d'équipement de chaque véhicule (y compris les corrections manuelles). Les administrateurs devront ré-entrer l'équipement correct au besoin.\n\nContinuer?")) return;
                  const toastId = toast.loading("Nettoyage de l'équipement en cours…");
                  try {
                    const res = await axios.post(`${API}/admin/vehicles/clear-all-features`, {}, { headers: getAuthHeader() });
                    toast.success(`${res.data.cleared} véhicules nettoyés`, { id: toastId });
                    fetchVehicles();
                  } catch (e) {
                    toast.error(`Échec: ${e?.response?.data?.detail || e.message}`, { id: toastId });
                  }
                }}
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="clear-all-features-button"
                title="Vider l'équipement de tous les véhicules — à utiliser pour effacer les suggestions AI incorrectes"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Effacer équipements</span>
                <span className="sm:hidden">Reset</span>
              </Button>
            )}
            {isAdmin() && (
              <Button
                onClick={async () => {
                  if (!window.confirm('Décoder tous les véhicules sans moteur (sauf vendus/réservés/hors-ligne) via VMR?')) return;
                  const toastId = toast.loading('Décodage VMR en cours — cela peut prendre quelques minutes…');
                  try {
                    const res = await axios.post(`${API}/admin/vehicles/backfill-engine-features`, {}, {
                      headers: getAuthHeader(),
                      timeout: 600000,
                    });
                    toast.success(
                      `VMR: ${res.data.updated} mis à jour · ${res.data.failed} ignorés · ${res.data.checked} vérifiés${res.data.quota_hit ? ' (quota atteint)' : ''}`,
                      { id: toastId }
                    );
                    fetchVehicles();
                  } catch (e) {
                    toast.error(`Échec backfill: ${e?.response?.data?.detail || e.message}`, { id: toastId });
                  }
                }}
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="backfill-engine-features-button"
                title="Appelle VMR pour chaque véhicule sans moteur"
              >
                <Zap className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Décoder VIN manquants</span>
                <span className="sm:hidden">VMR</span>
              </Button>
            )}
            {isAdmin() && (
              <Button
                onClick={async () => {
                  if (!window.confirm("Effacer les erreurs vidéo périmées pour permettre une nouvelle tentative?\n\nCeci réinitialise les véhicules bloqués en 'failed/queued/generating' pour que PixVerse puisse les retenter.")) return;
                  const toastId = toast.loading('Nettoyage des erreurs vidéo…');
                  try {
                    const res = await axios.post(`${API}/admin/vehicles/clear-video-errors`, {}, { headers: getAuthHeader() });
                    toast.success(`${res.data.cleared} véhicules réinitialisés — cliquez maintenant "Générer vidéos manquantes" pour relancer`, { id: toastId, duration: 7000 });
                    fetchVehicles();
                  } catch (e) {
                    toast.error(`Échec: ${e?.response?.data?.detail || e.message}`, { id: toastId });
                  }
                }}
                variant="outline"
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="clear-video-errors-button"
                title="Efface les erreurs vidéo périmées — à utiliser après avoir ajouté des crédits PixVerse"
              >
                <X className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Reset erreurs vidéo</span>
                <span className="sm:hidden">Reset vidéo</span>
              </Button>
            )}
            {isAdmin() && (
              <Button
                onClick={async () => {
                  const ok = window.confirm(
                    "Générer une vidéo PixVerse pour chaque véhicule sans vidéo (sauf vendus/réservés/hors-ligne) ?\n\n" +
                    "⚠️ ~235 crédits PixVerse par vidéo (~13 s, 720p). Chaque vidéo prend 2-5 min. Les tâches tournent en arrière-plan."
                  );
                  if (!ok) return;
                  const toastId = toast.loading('Mise en file des générations vidéo…');
                  try {
                    const res = await axios.post(`${API}/admin/vehicles/backfill-videos`, {}, {
                      headers: getAuthHeader(),
                      timeout: 120000,
                    });
                    toast.success(
                      `PixVerse: ${res.data.queued}/${res.data.eligible} vidéos mises en file (concurrence ${res.data.concurrency}). Les vidéos apparaîtront d'ici 2-5 min chacune.`,
                      { id: toastId, duration: 8000 }
                    );
                  } catch (e) {
                    toast.error(`Échec backfill vidéos: ${e?.response?.data?.detail || e.message}`, { id: toastId });
                  }
                }}
                variant="outline"
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="backfill-videos-button"
                title="Génère une vidéo cinématique (style côtier) à partir de la 1re photo pour chaque véhicule sans vidéo"
              >
                <Film className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Générer vidéos manquantes</span>
                <span className="sm:hidden">Vidéos</span>
              </Button>
            )}
            {isAdmin() && (
              <Button
                onClick={() => replaceVideos(null)}
                variant="outline"
                className="border-purple-300 text-purple-800 hover:bg-purple-50"
                data-testid="replace-all-videos-button"
                title="Régénère toutes les vidéos IA (PixVerse) — les vidéos téléversées manuellement sont conservées"
              >
                <Film className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Remplacer toutes les vidéos IA</span>
                <span className="sm:hidden">Remplacer</span>
              </Button>
            )}
            {isAdmin() && (
              <Button
                onClick={() => setStylesManagerOpen(true)}
                variant="outline"
                className="border-purple-300 text-purple-800 hover:bg-purple-50"
                data-testid="video-styles-button"
              >
                <Film className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Styles vidéo</span>
                <span className="sm:hidden">Styles</span>
              </Button>
            )}
            {isAdmin() && pendingVideoCount > 0 && (
              <Button
                onClick={() => setReview({})}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="pending-videos-button"
              >
                <Film className="h-4 w-4 mr-2" />
                Vidéos à approuver ({pendingVideoCount})
              </Button>
            )}
            {isAdmin() && (
              <CarfaxSyncButton headers={getAuthHeader()} onDone={fetchVehicles} />
            )}
            {isAdmin() && (
              <CarfaxMissingButton headers={getAuthHeader()} onChanged={fetchVehicles} />
            )}
            {isAdmin() && (
              <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-vehicle-button">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un véhicule
              </Button>
            )}
          </div>
        </div>

        {/* KPI Section - Collapsible */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* KPI Header - Always visible */}
          <button
            onClick={() => setShowKpiCards(!showKpiCards)}
            className="w-full flex items-center justify-between px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-4 text-sm">
              <span className="font-semibold text-slate-700">Statistiques</span>
              <div className="flex items-center gap-3 text-slate-500">
                <span><strong className="text-blue-600">{kpiStats.activeCount}</strong> en stock</span>
                <span>·</span>
                <span><strong className="text-green-600">${Math.round(kpiStats.totalCost / 1000)}K</strong> invest.</span>
                <span>·</span>
                <span><strong className="text-orange-500">{kpiStats.avgAge}j</strong> âge moy.</span>
              </div>
            </div>
            {showKpiCards ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </button>
          
          {/* KPI Content - Collapsible */}
          {showKpiCards && (
            <div className="p-3 space-y-3">
              {/* Top Row - Compact Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-slate-50 rounded-lg p-2 border-l-3 border-l-blue-500">
                  <p className="text-[10px] text-slate-500 uppercase">En Stock</p>
                  <p className="text-xl font-bold text-blue-600">{kpiStats.activeCount}</p>
                  <div className="text-[10px] text-slate-500 mt-1">
                    D:{kpiStats.available} · R:{kpiStats.reserved} · <span className="text-orange-500">V:{kpiStats.sold}</span>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2 border-l-3 border-l-green-500">
                  <p className="text-[10px] text-slate-500 uppercase">Invest. Total</p>
                  <p className="text-xl font-bold text-green-600">${Math.round(kpiStats.totalCost / 1000)}K</p>
                  <p className="text-[10px] text-slate-500 mt-1">Val: ${Math.round(kpiStats.totalValue / 1000)}K</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2 border-l-3 border-l-purple-500">
                  <p className="text-[10px] text-slate-500 uppercase">Invest. Moy</p>
                  <p className="text-xl font-bold text-purple-600">${Math.round(kpiStats.avgCost / 1000)}K</p>
                  <p className="text-[10px] text-slate-500 mt-1">par véhicule</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2 border-l-3 border-l-orange-500">
                  <p className="text-[10px] text-slate-500 uppercase">Âge Moy</p>
                  <p className="text-xl font-bold text-orange-500">{kpiStats.avgAge}</p>
                  <p className="text-[10px] text-slate-500 mt-1">jours</p>
                </div>
              </div>
              
              {/* Age Bracket Cards - Compact */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setAgeFilter(ageFilter === '0-45' ? null : '0-45')}
                  className={`text-left rounded-lg p-2 transition-all ${
                    ageFilter === '0-45' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-green-50 hover:bg-green-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-green-700">0-45j</span>
                    <span className="text-[10px] text-slate-500">{age0to45.percentage}%</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{age0to45.count}</p>
                  <div className="text-[10px] text-green-700 mt-1">
                    ${Math.round(age0to45.totalCost / 1000)}K · 
                    <span className={age0to45.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {age0to45.totalProfit >= 0 ? '+' : ''}{(age0to45.totalProfit / 1000).toFixed(1)}K
                    </span>
                  </div>
                </button>
                
                <button
                  onClick={() => setAgeFilter(ageFilter === '46-89' ? null : '46-89')}
                  className={`text-left rounded-lg p-2 transition-all ${
                    ageFilter === '46-89' ? 'bg-yellow-100 ring-2 ring-yellow-500' : 'bg-yellow-50 hover:bg-yellow-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-yellow-700">46-89j</span>
                    <span className="text-[10px] text-slate-500">{age46to89.percentage}%</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-600">{age46to89.count}</p>
                  <div className="text-[10px] text-yellow-700 mt-1">
                    ${Math.round(age46to89.totalCost / 1000)}K · 
                    <span className={age46to89.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {age46to89.totalProfit >= 0 ? '+' : ''}{(age46to89.totalProfit / 1000).toFixed(1)}K
                    </span>
                  </div>
                </button>
                
                <button
                  onClick={() => setAgeFilter(ageFilter === '90+' ? null : '90+')}
                  className={`text-left rounded-lg p-2 transition-all ${
                    ageFilter === '90+' ? 'bg-red-100 ring-2 ring-red-500' : 'bg-red-50 hover:bg-red-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-red-700">90+j</span>
                    <span className="text-[10px] text-slate-500">{age90plus.percentage}%</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{age90plus.count}</p>
                  <div className="text-[10px] text-red-700 mt-1">
                    ${Math.round(age90plus.totalCost / 1000)}K · 
                    <span className={age90plus.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {age90plus.totalProfit >= 0 ? '+' : ''}{(age90plus.totalProfit / 1000).toFixed(1)}K
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Search and Status Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par marque, modèle, VIN, couleur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800"
              data-testid="search-vehicles"
            />
          </div>
          
          {/* View Toggle Buttons */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
              title="Vue liste compacte"
              data-testid="view-mode-list"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'card'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
              title="Vue cartes"
              data-testid="view-mode-card"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          
          {/* Status Filter Buttons */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setStatusFilter(['available'])}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                statusFilter.length === 1 && statusFilter[0] === 'available'
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              ✓ Dispo ({kpiStats.available})
            </button>
            <button
              onClick={() => setStatusFilter(['reserved'])}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                statusFilter.length === 1 && statusFilter[0] === 'reserved'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              ◐ Réservé ({kpiStats.reserved})
            </button>
            <button
              onClick={() => setStatusFilter(['sold'])}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                statusFilter.length === 1 && statusFilter[0] === 'sold'
                  ? 'bg-white text-red-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              ✗ Vendu ({kpiStats.sold})
            </button>
            <button
              onClick={() => setStatusFilter(['offline'])}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                statusFilter.length === 1 && statusFilter[0] === 'offline'
                  ? 'bg-white text-slate-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              ○ H-L ({kpiStats.offline})
            </button>
            <div className="w-px h-5 bg-slate-300 mx-1"></div>
            <button
              onClick={() => setStatusFilter(['available', 'reserved', 'sold', 'offline'])}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                statusFilter.length === 4
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Tous ({kpiStats.total})
            </button>
            <div className="w-px h-5 bg-slate-300 mx-1"></div>
            {priceChangedVehicles.length > 0 && (
              <button
                onClick={reprintPriceChanged}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                title="Imprimer à nouveau les étiquettes des véhicules dont le prix a changé"
                data-testid="reprint-price-changed-btn"
              >
                <Printer className="h-3.5 w-3.5" />
                Réimprimer prix changés ({priceChangedVehicles.length})
              </button>
            )}
            <button
              onClick={handleBulkPrint}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              title="Cochez des véhicules (ou la case du haut pour tout sélectionner), puis imprimez"
              data-testid="toolbar-bulk-print-btn"
            >
              <Printer className="h-3.5 w-3.5" />
              Imprimer étiquettes{selectedVehicles.length ? ` (${selectedVehicles.length})` : ''}
            </button>
            {/* Reset Button */}
            {(statusFilter.length > 0 || ageFilter) && (
              <>
                <div className="w-px h-5 bg-slate-300 mx-1"></div>
                <button
                  onClick={() => { setStatusFilter([]); setAgeFilter(null); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-md text-slate-600 hover:text-slate-800 hover:bg-white transition-all"
                >
                  Reset
                </button>
              </>
            )}
          </div>
          
          {/* Age Filter Indicator */}
          {ageFilter && (
            <div className={`px-3 py-1.5 text-xs font-medium rounded-full flex items-center gap-2 ${
              ageFilter === '0-45' ? 'bg-green-100 text-green-700' :
              ageFilter === '46-89' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              Filtre: {ageFilter} jours
              <button 
                onClick={() => setAgeFilter(null)}
                className="hover:opacity-70"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Bulk Selection Bar */}
        {selectedVehicles.length > 0 && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={selectedVehicles.length === filteredVehicles.length && filteredVehicles.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm font-medium text-blue-800">
                  {selectedVehicles.length} véhicule(s) sélectionné(s)
                </span>
              </div>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                data-testid="clear-selection-btn"
              >
                Tout désélectionner
              </button>
            </div>
            <div className="flex items-center gap-2">
            {isAdmin() && (
              <button
                onClick={() => replaceVideos(selectedVehicles)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                data-testid="bulk-regenerate-videos-btn"
              >
                <Film className="h-4 w-4" />
                <span className="hidden sm:inline">Régénérer {selectedVehicles.length} vidéo(s)</span>
                <span className="sm:hidden">{selectedVehicles.length}</span>
              </button>
            )}
            <button
              onClick={handleBulkPrint}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              data-testid="bulk-print-stickers-btn"
            >
              <Printer className="h-4 w-4" />
              Imprimer {selectedVehicles.length} étiquette(s)
            </button>
            </div>
          </div>
        )}


        {/* Loading State */}
        {loading && (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-500" />
            <p className="text-slate-500 text-sm">Chargement...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredVehicles.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Car className="h-12 w-12 mx-auto mb-2 text-slate-300" />
            <p className="text-slate-500">Aucun véhicule trouvé</p>
          </div>
        )}

        {/* List View */}
        {!loading && filteredVehicles.length > 0 && viewMode === 'list' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
            {/* List Header */}
            <div className="grid grid-cols-[28px_65px_40px_170px_55px_55px_55px_70px_65px_65px_65px_65px_minmax(70px,1fr)_65px_100px] gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 min-w-[1200px] items-center">
              <div className="flex items-center justify-center">
                <Checkbox 
                  checked={selectedVehicles.length === filteredVehicles.length && filteredVehicles.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="list-select-all"
                />
              </div>
              <div>Photo</div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1" onClick={() => handleSort('inventory_age')}>
                Âge {getSortIcon('inventory_age')}
              </div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1" onClick={() => handleSort('vehicle')}>
                Véhicule / Stock {getSortIcon('vehicle')}
              </div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1" onClick={() => handleSort('mileage')}>
                Km {getSortIcon('mileage')}
              </div>
              <div>Trans.</div>
              <div>Tract.</div>
              <div>CARFAX</div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1 text-orange-600" onClick={() => handleSort('cost')}>
                Coût {getSortIcon('cost')}
              </div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1" onClick={() => handleSort('price')}>
                Prix {getSortIcon('price')}
              </div>
              <div className="text-purple-600">Rég.</div>
              <div className="cursor-pointer hover:text-blue-600 flex items-center gap-1 text-green-600" onClick={() => handleSort('profit')}>
                Profit {getSortIcon('profit')}
              </div>
              <div>Titre</div>
              <div>Statut</div>
              <div className="text-center">Actions</div>
            </div>
            
            {/* List Items */}
            {filteredVehicles.map((vehicle, index) => (
              <div 
                key={vehicle.id}
                className={`grid grid-cols-[28px_65px_40px_170px_55px_55px_55px_70px_65px_65px_65px_65px_minmax(70px,1fr)_65px_100px] gap-2 px-3 py-3 items-center border-b border-slate-100 last:border-b-0 hover:bg-blue-50/50 transition-colors min-w-[1200px] ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                } ${vehicle.status === 'sold' || vehicle.status === 'offline' ? 'opacity-50' : ''} ${
                  selectedVehicles.includes(vehicle.id) ? 'bg-blue-50 ring-1 ring-blue-200' : ''
                }`}
                data-testid={`vehicle-row-${vehicle.id}`}
              >
                {/* Checkbox for bulk selection */}
                <div className="flex items-center justify-center">
                  <Checkbox 
                    checked={selectedVehicles.includes(vehicle.id)}
                    onCheckedChange={() => toggleVehicleSelection(vehicle.id)}
                    data-testid={`select-vehicle-${vehicle.id}`}
                  />
                </div>
                
                {/* Thumbnail */}
                <div className="relative w-14 h-10 rounded overflow-hidden bg-slate-100 flex-shrink-0">
                  <img 
                    src={vehicle.images?.[0] ? normalizeImageUrl(vehicle.images[0]) : FALLBACK_PLACEHOLDER} 
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { if (e.target.src !== FALLBACK_PLACEHOLDER) e.target.src = FALLBACK_PLACEHOLDER; }}
                  />
                  <span className={`absolute bottom-0 right-0 text-[7px] px-1 py-0.5 font-semibold rounded-tl ${
                    (vehicle.images?.length || 0) === 0 ? 'bg-red-500 text-white' : 
                    (vehicle.images?.length || 0) < 5 ? 'bg-amber-500 text-white' : 'bg-green-600 text-white'
                  }`}>
                    {vehicle.images?.length || 0}
                  </span>
                  {(() => {
                    const s = vehicle.video_generation_status;
                    if (s === 'pending_review') return (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (isAdmin()) setReview({ vehicleId: vehicle.id }); }}
                        className="absolute top-0 left-0 w-3 h-3 flex items-center justify-center text-[7px] text-white font-bold rounded-br bg-amber-500 animate-pulse"
                        title="Nouvelle vidéo à approuver — cliquer pour voir"
                        data-testid={`video-status-${vehicle.id}`}
                      >?</button>
                    );
                    const ready = !!vehicle.video_url && (s === 'ready' || !s);
                    const label = ready ? '●' : s === 'queued' ? '⏳' : s === 'generating' ? '⚙' : s === 'failed' ? '!' : null;
                    const color = ready ? 'bg-emerald-500' : s === 'queued' ? 'bg-slate-400' : s === 'generating' ? 'bg-blue-500 animate-pulse' : s === 'failed' ? 'bg-red-500' : null;
                    if (!label) return null;
                    return (
                      <span
                        className={`absolute top-0 left-0 w-3 h-3 flex items-center justify-center text-[7px] text-white font-bold rounded-br ${color}`}
                        title={ready ? 'Vidéo prête' : s === 'queued' ? 'Vidéo en file d\'attente' : s === 'generating' ? 'Génération vidéo…' : s === 'failed' ? `Échec: ${vehicle.video_generation_error || 'inconnu'}` : ''}
                        data-testid={`video-status-${vehicle.id}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
                
                {/* Age (days in inventory from Row A) */}
                <div className={`text-[11px] font-medium text-center ${
                  (() => {
                    const age = calculateInventoryAge(vehicle);
                    if (age === '—') return 'text-slate-400';
                    if (age >= 90) return 'text-red-600';
                    if (age >= 46) return 'text-yellow-600';
                    return 'text-green-600';
                  })()
                }`}>
                  {calculateInventoryAge(vehicle)}
                </div>
                
                {/* Vehicle Info + Stock Number - Combined */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 text-[11px] truncate">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </span>
                    {vehicle.is_featured && (
                      <Star className="h-3 w-3 text-amber-500 fill-amber-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-slate-500 truncate">{vehicle.trim || '—'}</span>
                    <span className="text-slate-300">|</span>
                    {editingCell?.vehicleId === vehicle.id && editingCell?.field === 'stock_number' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, vehicle.id, 'stock_number')}
                        onBlur={() => saveInlineEdit(vehicle.id, 'stock_number')}
                        autoFocus
                        className="w-20 text-[11px] px-1 py-0.5 border border-blue-400 rounded bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Stock #"
                      />
                    ) : (
                      <span 
                        className="text-[11px] text-blue-600 font-medium cursor-pointer hover:text-blue-800 hover:underline"
                        onClick={() => startEditing(vehicle.id, 'stock_number', vehicle.stock_number)}
                        title="Cliquez pour modifier"
                      >
                        #{vehicle.stock_number || '—'}
                      </span>
                    )}
                  </div>
                  {/* Full VIN with Copy Button */}
                  {vehicle.vin && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded select-all">
                        {vehicle.vin}
                      </span>
                      <button
                        onClick={() => copyVin(vehicle.vin)}
                        className="p-0.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Copier le VIN"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Mileage - Inline Editable */}
                <div>
                  {editingCell?.vehicleId === vehicle.id && editingCell?.field === 'mileage' ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, vehicle.id, 'mileage')}
                      onBlur={() => saveInlineEdit(vehicle.id, 'mileage')}
                      autoFocus
                      className="w-full text-[11px] px-2 py-1 border border-blue-400 rounded bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Km"
                    />
                  ) : (
                    <div 
                      className="text-[11px] text-slate-600 cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                      onClick={() => startEditing(vehicle.id, 'mileage', vehicle.mileage)}
                      title="Cliquez pour modifier"
                    >
                      {formatMileage(vehicle.mileage)}
                    </div>
                  )}
                </div>
                
                {/* Transmission - Inline Editable Select */}
                <div>
                  <select
                    value={(() => {
                      const t = (vehicle.transmission || '').toLowerCase();
                      if (t === 'automatique' || t === 'automatic' || t === 'auto') return 'automatic';
                      if (t === 'manuelle' || t === 'manual') return 'manual';
                      if (t === 'cvt') return 'cvt';
                      return 'automatic';
                    })()}
                    onChange={async (e) => {
                      if (isViewer()) return;
                      try {
                        await axios.put(`${API}/vehicles/${vehicle.id}`, { transmission: e.target.value }, { headers: getAuthHeader() });
                        setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, transmission: e.target.value } : v));
                        toast.success('Mis à jour');
                      } catch (error) { toast.error('Erreur'); }
                    }}
                    disabled={isViewer()}
                    className={`text-[11px] border border-slate-200 rounded px-1 py-1 bg-white text-slate-600 ${isViewer() ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-blue-400'} focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                  >
                    <option value="automatic">Auto</option>
                    <option value="manual">Manuel</option>
                    <option value="cvt">CVT</option>
                  </select>
                </div>
                
                {/* Traction/Drivetrain - Inline Editable Select */}
                <div>
                  <select
                    value={(vehicle.drivetrain || 'fwd').toLowerCase()}
                    onChange={async (e) => {
                      if (isViewer()) return;
                      try {
                        await axios.put(`${API}/vehicles/${vehicle.id}`, { drivetrain: e.target.value }, { headers: getAuthHeader() });
                        setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, drivetrain: e.target.value } : v));
                        toast.success('Mis à jour');
                      } catch (error) { toast.error('Erreur'); }
                    }}
                    disabled={isViewer()}
                    className={`text-[11px] border border-slate-200 rounded px-1 py-1 bg-white text-slate-600 ${isViewer() ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-blue-400'} focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                  >
                    <option value="fwd">FWD</option>
                    <option value="rwd">RWD</option>
                    <option value="awd">AWD</option>
                    <option value="4wd">4x4</option>
                  </select>
                </div>
                
                {/* CARFAX (read-only, synced from CARFAX Canada) */}
                <div className="flex flex-col gap-0.5 text-[10px] font-semibold" data-testid={`admin-carfax-${vehicle.id}`}>
                  {vehicle.carfax_report_url ? (
                    <>
                      {(vehicle.carfax_badges || []).includes('AccidentFree') && <span className="text-green-700">✓ 0 Acc</span>}
                      {(vehicle.carfax_badges || []).includes('OneOwner') && <span className="text-blue-700">✓ 1 Prop</span>}
                      {!(vehicle.carfax_badges || []).length && <span className="text-slate-500">Rapport</span>}
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </div>
                
                {/* Cost (Read-only from Google Sheets) */}
                <div className="text-[11px] text-orange-700 font-medium px-1">
                  {vehicle.cost > 0 ? `${vehicle.cost.toLocaleString('fr-CA')}$` : '—'}
                </div>
                
                {/* Price (Retail) - Inline Editable */}
                <div>
                  {editingCell?.vehicleId === vehicle.id && editingCell?.field === 'price' ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, vehicle.id, 'price')}
                      onBlur={() => saveInlineEdit(vehicle.id, 'price')}
                      autoFocus
                      className="w-full text-[11px] font-semibold px-2 py-1 border border-blue-400 rounded bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Prix"
                    />
                  ) : (
                    <div 
                      className="font-semibold text-slate-800 text-[11px] cursor-pointer hover:text-green-600 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                      onClick={() => startEditing(vehicle.id, 'price', vehicle.price)}
                      title="Cliquez pour modifier"
                    >
                      {formatPrice(vehicle.price)}
                    </div>
                  )}
                  <PriceChangedBadge vehicle={vehicle} compact />
                  <div><span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-slate-500 whitespace-nowrap" title="Scans du code QR de l'étiquette" data-testid={`qr-scans-${vehicle.id}`}><QrCode className="h-2.5 w-2.5" />{vehicle.qr_scans || 0} scan{(vehicle.qr_scans || 0) > 1 ? 's' : ''}</span></div>
                </div>
                
                {/* Regular Price - Inline Editable */}
                <div>
                  {editingCell?.vehicleId === vehicle.id && editingCell?.field === 'regular_price' ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, vehicle.id, 'regular_price')}
                      onBlur={() => saveInlineEdit(vehicle.id, 'regular_price')}
                      autoFocus
                      className="w-full text-[11px] font-semibold px-2 py-1 border border-purple-400 rounded bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Rég."
                      data-testid={`inline-regular-price-${vehicle.id}`}
                    />
                  ) : (
                    <div 
                      className={`text-[11px] cursor-pointer hover:text-purple-600 hover:bg-purple-50 px-2 py-1 rounded transition-colors ${vehicle.regular_price ? 'font-semibold text-purple-700' : 'text-slate-400 italic'}`}
                      onClick={() => startEditing(vehicle.id, 'regular_price', vehicle.regular_price || '')}
                      title="Cliquez pour modifier le prix régulier"
                      data-testid={`regular-price-cell-${vehicle.id}`}
                    >
                      {vehicle.regular_price ? formatPrice(vehicle.regular_price) : '+ Rég.'}
                    </div>
                  )}
                </div>

                {/* Profit (Calculated) */}
                <div className={`text-[11px] font-semibold px-1 ${
                  (vehicle.profit || 0) > 0 ? 'text-green-600' : 
                  (vehicle.profit || 0) < 0 ? 'text-red-600' : 'text-slate-400'
                }`}>
                  {vehicle.profit ? (
                    <>
                      {vehicle.profit > 0 ? '+' : ''}{vehicle.profit.toLocaleString('fr-CA')}$
                      {vehicle.price > 0 && vehicle.cost > 0 && (
                        <span className="text-[9px] ml-0.5 opacity-70">
                          ({Math.round((vehicle.profit / vehicle.price) * 100)}%)
                        </span>
                      )}
                    </>
                  ) : '—'}
                </div>
                
                {/* Title - Inline Editable */}
                <div>
                  {editingCell?.vehicleId === vehicle.id && editingCell?.field === 'title' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, vehicle.id, 'title')}
                      onBlur={() => saveInlineEdit(vehicle.id, 'title')}
                      autoFocus
                      className="w-full text-[11px] px-2 py-1 border border-blue-400 rounded bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Titre / Sous-titre"
                    />
                  ) : (
                    <div 
                      className="text-[11px] text-slate-600 cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded transition-colors truncate"
                      onClick={() => startEditing(vehicle.id, 'title', vehicle.title)}
                      title={vehicle.title || 'Cliquez pour ajouter un titre'}
                    >
                      {vehicle.title || <span className="text-slate-400 italic">+ Titre</span>}
                    </div>
                  )}
                </div>
                
                {/* Status */}
                <div>
                  <select
                    value={vehicle.status}
                    onChange={async (e) => {
                      if (isViewer()) return;
                      const newStatus = e.target.value;
                      // Update UI immediately
                      setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, status: newStatus } : v));
                      try {
                        await axios.put(`${API}/vehicles/${vehicle.id}`, { status: newStatus }, { headers: getAuthHeader() });
                        toast.success('Statut mis à jour');
                      } catch (error) { 
                        // Revert on error
                        setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, status: vehicle.status } : v));
                        toast.error('Erreur'); 
                      }
                    }}
                    disabled={isViewer()}
                    className={`text-[10px] border rounded px-1 py-0.5 font-medium w-full ${
                      vehicle.status === 'available' ? 'bg-green-50 border-green-200 text-green-700' :
                      vehicle.status === 'new_arrival' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                      vehicle.status === 'reserved' ? 'bg-purple-50 border-purple-200 text-purple-700' :
                      vehicle.status === 'sold' ? 'bg-red-50 border-red-200 text-red-700' :
                      'bg-slate-50 border-slate-200 text-slate-600'
                    } ${isViewer() ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="available">✓ Dispo</option>
                    <option value="new_arrival">★ Nouv.</option>
                    <option value="reserved">◐ Rés.</option>
                    <option value="sold">✗ Vendu</option>
                    <option value="offline">○ H-L</option>
                  </select>
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-center gap-0.5 flex-nowrap">
                  <button
                    onClick={() => setStickerVehicle(vehicle)}
                    data-testid={`print-sticker-${vehicle.id}`}
                    className="p-1 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Imprimer étiquette"
                    data-testid={`print-sticker-${vehicle.id}`}
                  >
                    <Printer className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => postToFacebook(vehicle)}
                    className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                    title="Publier sur Facebook"
                    data-testid={`fb-post-${vehicle.id}`}
                  >
                    <Facebook className="h-3.5 w-3.5" />
                  </button>
                  {isAdmin() && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/vehicles/${vehicle.id}`, { is_featured: !vehicle.is_featured }, { headers: getAuthHeader() });
                            setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, is_featured: !vehicle.is_featured } : v));
                          } catch (error) { toast.error('Erreur'); }
                        }}
                        className="p-1 hover:bg-amber-50 rounded transition-colors"
                        title="Vedette"
                        data-testid={`feature-${vehicle.id}`}
                      >
                        <Star className={`h-3.5 w-3.5 ${vehicle.is_featured ? 'text-amber-500 fill-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
                      </button>
                      <button 
                        onClick={() => handleOpenEdit(vehicle)} 
                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Modifier"
                        data-testid={`edit-${vehicle.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button 
                        onClick={() => handleOpenDelete(vehicle)} 
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Supprimer"
                        data-testid={`delete-${vehicle.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Card Grid Layout */}
        {!loading && filteredVehicles.length > 0 && viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredVehicles.map((vehicle) => (
              <div 
                key={vehicle.id}
                className={`bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all ${
                  vehicle.status === 'sold' || vehicle.status === 'offline' ? 'opacity-50' : ''
                }`}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] bg-slate-100">
                  <img 
                    src={vehicle.images?.[0] ? normalizeImageUrl(vehicle.images[0]) : FALLBACK_PLACEHOLDER} 
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { if (e.target.src !== FALLBACK_PLACEHOLDER) e.target.src = FALLBACK_PLACEHOLDER; }}
                  />
                  {/* Photo count badge */}
                  <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    (vehicle.images?.length || 0) === 0 ? 'bg-red-500 text-white' : 
                    (vehicle.images?.length || 0) < 5 ? 'bg-amber-500 text-white' : 'bg-green-600 text-white'
                  }`}>
                    {vehicle.images?.length || 0} photos
                  </span>
                  {/* Status badge */}
                  <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    vehicle.status === 'available' ? 'bg-green-500 text-white' :
                    vehicle.status === 'new_arrival' ? 'bg-blue-500 text-white' :
                    vehicle.status === 'reserved' ? 'bg-purple-500 text-white' :
                    vehicle.status === 'sold' ? 'bg-red-500 text-white' :
                    'bg-slate-500 text-white'
                  }`}>
                    {vehicle.status === 'available' ? '✓ Disponible' :
                     vehicle.status === 'new_arrival' ? '★ Nouveau' :
                     vehicle.status === 'reserved' ? '◐ Réservé' :
                     vehicle.status === 'sold' ? '✗ Vendu' : '○ Hors-Ligne'}
                  </span>
                  {/* Featured star */}
                  {vehicle.is_featured && (
                    <span className="absolute bottom-2 left-2 bg-amber-400 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                      <Star className="h-3 w-3 fill-white" /> Vedette
                    </span>
                  )}
                  {/* Video generation badge */}
                  {(() => {
                    const s = vehicle.video_generation_status;
                    if (s === 'pending_review') return (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (isAdmin()) setReview({ vehicleId: vehicle.id }); }}
                        className="absolute top-10 left-2 bg-amber-500 hover:bg-amber-600 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        title="Cliquer pour voir et approuver"
                        data-testid={`video-status-${vehicle.id}`}
                      >
                        Vidéo à approuver
                      </button>
                    );
                    const ready = !!vehicle.video_url && (s === 'ready' || !s);
                    if (!s && !vehicle.video_url) return null;
                    const label = ready ? '🎬 Vidéo' : s === 'queued' ? '⏳ En file' : s === 'generating' ? '⚙ Génération…' : s === 'failed' ? '⚠ Vidéo échouée' : null;
                    const color = ready ? 'bg-emerald-500' : s === 'queued' ? 'bg-slate-500' : s === 'generating' ? 'bg-blue-500 animate-pulse' : s === 'failed' ? 'bg-red-500' : null;
                    if (!label) return null;
                    return (
                      <span
                        className={`absolute top-10 left-2 ${color} text-white text-[10px] px-2 py-0.5 rounded-full font-semibold`}
                        title={s === 'failed' ? vehicle.video_generation_error || 'Échec inconnu' : ''}
                        data-testid={`video-status-${vehicle.id}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                  {/* Price overlay */}
                  <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-lg shadow">
                    <span 
                      className="font-bold text-slate-800 text-lg cursor-pointer hover:text-green-600"
                      onClick={() => startEditing(vehicle.id, 'price', vehicle.price)}
                    >
                      {formatPrice(vehicle.price)}
                    </span>
                  </div>
                  <div className="absolute bottom-12 right-2"><PriceChangedBadge vehicle={vehicle} /></div>
                  <div className="absolute bottom-2 left-2 bg-white/90 rounded px-1.5 py-0.5"><span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-slate-500 whitespace-nowrap" title="Scans du code QR de l'étiquette" data-testid={`qr-scans-${vehicle.id}`}><QrCode className="h-2.5 w-2.5" />{vehicle.qr_scans || 0} scan{(vehicle.qr_scans || 0) > 1 ? 's' : ''}</span></div>
                </div>
                
                {/* Content */}
                <div className="p-3">
                  {/* Title & Stock */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-slate-800 text-sm leading-tight truncate">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                      <p className="text-[11px] text-slate-500 truncate">{vehicle.trim || 'Version non spécifiée'}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-blue-600 font-semibold">#{vehicle.stock_number || '—'}</p>
                      <p className="text-[9px] text-slate-400 font-mono">{vehicle.vin ? `...${vehicle.vin.slice(-6)}` : '—'}</p>
                    </div>
                  </div>
                  
                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-3 text-[11px]">
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Gauge className="h-3 w-3 text-slate-400" />
                      <span>{formatMileage(vehicle.mileage)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Palette className="h-3 w-3 text-slate-400" />
                      <span className="w-2.5 h-2.5 rounded-full border" style={{ backgroundColor: getColorCode(vehicle.exterior_color) }}></span>
                      <span className="truncate">{(vehicle.exterior_color || '—').split('\n')[0]}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Settings className="h-3 w-3 text-slate-400" />
                      <span>{vehicle.transmission === 'automatic' ? 'Auto' : vehicle.transmission === 'manual' ? 'Manuel' : vehicle.transmission || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Car className="h-3 w-3 text-slate-400" />
                      <span>{vehicle.drivetrain?.toUpperCase() || '—'}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <select
                      value={vehicle.status}
                      onChange={async (e) => {
                        if (isViewer()) return;
                        const newStatus = e.target.value;
                        // Update UI immediately
                        setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, status: newStatus } : v));
                        try {
                          await axios.put(`${API}/vehicles/${vehicle.id}`, { status: newStatus }, { headers: getAuthHeader() });
                          toast.success('Statut mis à jour');
                        } catch (error) { 
                          // Revert on error
                          setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, status: vehicle.status } : v));
                          toast.error('Erreur'); 
                        }
                      }}
                      className={`text-[10px] border border-slate-200 rounded px-2 py-1 bg-white text-slate-600 ${isViewer() ? 'cursor-not-allowed opacity-60' : ''}`}
                      disabled={isViewer()}
                    >
                      <option value="available">✓ Disponible</option>
                      <option value="new_arrival">★ Nouvel arrivage</option>
                      <option value="reserved">◐ Réservé</option>
                      <option value="sold">✗ Vendu</option>
                      <option value="offline">○ Hors-Ligne</option>
                    </select>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setStickerVehicle(vehicle)}
                    data-testid={`print-sticker-${vehicle.id}`}
                        className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Imprimer étiquette"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => postToFacebook(vehicle)}
                        className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Publier sur Facebook"
                      >
                        <Facebook className="h-4 w-4" />
                      </button>
                      {isAdmin() && (
                        <>
                          <button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/vehicles/${vehicle.id}`, { is_featured: !vehicle.is_featured }, { headers: getAuthHeader() });
                                setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, is_featured: !vehicle.is_featured } : v));
                              } catch (error) { toast.error('Erreur'); }
                            }}
                            className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                          >
                            <Star className={`h-4 w-4 ${vehicle.is_featured ? 'text-amber-500 fill-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
                          </button>
                          <button onClick={() => handleOpenEdit(vehicle)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Pencil className="h-4 w-4" />
                          </button>
                          {vehicle.has_real_photo && (
                            <button
                            onClick={() => regenerateOne(vehicle)}
                            className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="Régénérer la vidéo PixVerse"
                            data-testid={`regenerate-video-${vehicle.id}`}
                          >
                            <Film className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => handleOpenDelete(vehicle)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Footer count */}
        {!loading && filteredVehicles.length > 0 && (
          <div className="mt-4 text-center text-sm text-slate-500">
            Affichage de {filteredVehicles.length} véhicule(s) — Vue {viewMode === 'list' ? 'liste' : 'cartes'}
          </div>
        )}

      </div>

      {/* Add/Edit Dialog - Professional Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-50 text-slate-900 max-w-5xl w-[95vw] h-[95vh] overflow-y-auto p-0">
          <DialogHeader className="p-6 pb-4 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Car className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {selectedVehicle ? 'Modifier le véhicule' : 'Ajouter un nouveau véhicule'}
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedVehicle ? `Modification de ${selectedVehicle.make} ${selectedVehicle.model}` : 'Remplissez les informations du véhicule'}
                </p>
              </div>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="vehicle-form">
            {/* VIN Decoder - Always visible */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-5 text-white">
              <div className="flex items-center gap-3 mb-3">
                <Hash className="h-5 w-5" />
                <div>
                  <h3 className="font-semibold">Décodeur VIN</h3>
                  <p className="text-blue-100 text-sm">Remplissage automatique des informations</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Input
                  value={form.vin}
                  onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                  placeholder="Entrez le VIN (17 caractères)"
                  maxLength={17}
                  className="bg-white/10 border-white/20 font-mono uppercase flex-1 text-white placeholder:text-blue-200"
                  data-testid="vehicle-vin"
                />
                <Button 
                  type="button" 
                  onClick={handleDecodeVin} 
                  disabled={decodingVin || form.vin.length !== 17}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-6 font-semibold"
                >
                  {decodingVin ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Décoder'}
                </Button>
              </div>
            </div>

            {/* Basic Info Section */}
            <SectionHeader id="basic" icon={Car} title="Informations de base" subtitle="Marque, modèle et identifiants" expanded={expandedSections.basic} onToggle={toggleSection}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Marque *</Label>
                  <Input
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                    placeholder="Toyota"
                    required
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Modèle *</Label>
                  <Input
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    placeholder="Camry"
                    required
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Version / Trim</Label>
                  <Input
                    value={form.trim}
                    onChange={(e) => setForm({ ...form, trim: e.target.value })}
                    placeholder="XLE, Sport..."
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">N° de stock</Label>
                  <Input
                    value={form.stock_number}
                    onChange={(e) => setForm({ ...form, stock_number: e.target.value })}
                    placeholder="STK-001"
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Moteur</Label>
                  <Input
                    value={form.engine || ''}
                    onChange={(e) => setForm({ ...form, engine: e.target.value })}
                    placeholder="3.5L V6"
                    className="bg-white border-slate-200 mt-1.5"
                    data-testid="vehicle-engine-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Année *</Label>
                  <Input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    min="1990"
                    max={new Date().getFullYear() + 1}
                    required
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Prix ($) *</Label>
                  <Input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    min="0"
                    step="1"
                    required
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Prix regulier ($)</Label>
                  <Input
                    type="number"
                    value={form.regular_price}
                    onChange={(e) => setForm({ ...form, regular_price: e.target.value })}
                    min="0"
                    step="1"
                    placeholder="Laisser vide si pas de promo"
                    className="bg-white border-slate-200 mt-1.5"
                    data-testid="regular-price-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Kilométrage *</Label>
                  <Input
                    type="number"
                    value={form.mileage}
                    onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                    min="0"
                    required
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Titre / Sous-titre</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Toit ouvrant, Cuir..."
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
              </div>
            </SectionHeader>

            {/* Specifications Section */}
            <SectionHeader id="specs" icon={Settings} title="Spécifications techniques" subtitle="Carrosserie, transmission et motorisation" expanded={expandedSections.specs} onToggle={toggleSection}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4">
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Type de carrosserie</Label>
                  <Select value={form.body_type} onValueChange={(v) => setForm({ ...form, body_type: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        { value: 'sedan', label: 'Berline' },
                        { value: 'suv', label: 'VUS' },
                        { value: 'truck', label: 'Camion' },
                        { value: 'coupe', label: 'Coupé' },
                        { value: 'hatchback', label: 'Hayon' },
                        { value: 'van', label: 'Fourgonnette' },
                        { value: 'convertible', label: 'Cabriolet' },
                      ].map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Transmission</Label>
                  <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">Automatique</SelectItem>
                      <SelectItem value="manual">Manuelle</SelectItem>
                      <SelectItem value="cvt">CVT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Traction</Label>
                  <Select value={form.drivetrain} onValueChange={(v) => setForm({ ...form, drivetrain: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fwd">Traction (FWD)</SelectItem>
                      <SelectItem value="rwd">Propulsion (RWD)</SelectItem>
                      <SelectItem value="awd">AWD</SelectItem>
                      <SelectItem value="4wd">4x4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Carburant</Label>
                  <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">Essence</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="electric">Électrique</SelectItem>
                      <SelectItem value="hybrid">Hybride</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Sièges</Label>
                  <Input
                    type="number"
                    value={form.seats}
                    onChange={(e) => setForm({ ...form, seats: e.target.value })}
                    placeholder="5"
                    min="2"
                    max="12"
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
              </div>
            </SectionHeader>

            {/* Colors Section */}
            <SectionHeader id="colors" icon={Palette} title="Couleurs" subtitle="Couleurs extérieure et intérieure" expanded={expandedSections.colors} onToggle={toggleSection}>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Couleur extérieure</Label>
                  <Input
                    value={form.exterior_color || ''}
                    onChange={(e) => setForm({ ...form, exterior_color: e.target.value })}
                    placeholder="Noir, Blanc, Argent..."
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-sm">Couleur intérieure</Label>
                  <Input
                    value={form.interior_color || ''}
                    onChange={(e) => setForm({ ...form, interior_color: e.target.value })}
                    placeholder="Cuir noir, Tissu gris..."
                    className="bg-white border-slate-200 mt-1.5"
                  />
                </div>
              </div>
            </SectionHeader>

            {/* Images Section */}
            <SectionHeader id="images" icon={Image} title="Photos du véhicule" subtitle={`${form.images.length} photo(s) ajoutée(s)`} expanded={expandedSections.images} onToggle={toggleSection}>
              <div className="pt-4">
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                    isDraggingFiles 
                      ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                      : 'border-slate-300 hover:border-blue-400 bg-white'
                  }`}
                  onDragEnter={handleFileDragEnter}
                  onDragLeave={handleFileDragLeave}
                  onDragOver={handleFileDragOver}
                  onDrop={handleFileDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer block">
                    {uploadingImages ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-3" />
                        <p className="text-slate-700 font-medium text-lg">Téléversement et optimisation...</p>
                        <p className="text-slate-500 text-sm">Les images sont converties en WebP</p>
                      </div>
                    ) : isDraggingFiles ? (
                      <div className="flex flex-col items-center py-4">
                        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-3 animate-pulse">
                          <Upload className="h-8 w-8 text-blue-600" />
                        </div>
                        <p className="text-blue-700 font-semibold text-lg">Déposez vos photos ici</p>
                        <p className="text-blue-500 text-sm">Relâchez pour téléverser</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                          <Upload className="h-8 w-8 text-slate-400" />
                        </div>
                        <p className="text-slate-700 font-medium text-lg">Glissez-déposez vos photos ici</p>
                        <p className="text-slate-500 text-sm mb-2">ou cliquez pour sélectionner</p>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                          PNG, JPG, WebP • Max 10MB par image
                        </span>
                      </div>
                    )}
                  </label>
                </div>

                {/* Image Previews */}
                {form.images.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500">Glissez-déposez pour réorganiser. La première photo sera l'image principale.</p>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, images: [] }))}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Supprimer toutes les photos
                      </button>
                    </div>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                      {form.images.map((url, index) => (
                        <div 
                          key={url + index} 
                          draggable
                          onDragStart={(e) => handleImageDragStart(e, index)}
                          onDragOver={handleImageDragOver}
                          onDrop={(e) => handleImageDrop(e, index)}
                          className={`relative group aspect-[4/3] cursor-move ${draggedImageIdx === index ? 'opacity-50 scale-95' : ''} transition-all`}
                        >
                          <img 
                            src={normalizeImageUrl(url)} 
                            alt={`Vehicle ${index + 1}`} 
                            className="w-full h-full object-cover rounded-lg border border-slate-200"
                            onError={(e) => {
                              // Retry once with a timestamp to bypass cache
                              if (!e.target.dataset.retried) {
                                e.target.dataset.retried = 'true';
                                const originalSrc = normalizeImageUrl(url);
                                e.target.src = `${originalSrc}${originalSrc.includes('?') ? '&' : '?'}t=${Date.now()}`;
                              } else {
                                // Show error placeholder
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="10" fill="%2394a3b8">Erreur</text></svg>';
                              }
                            }}
                          />
                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          {/* Position badge */}
                          <span className={`absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded ${index === 0 ? 'bg-blue-600 text-white' : 'bg-black/60 text-white'}`}>
                            {index === 0 ? 'Principal' : `#${index + 1}`}
                          </span>
                          {/* Arrow controls */}
                          <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => moveImage(index, index - 1)}
                                className="p-0.5 bg-white/90 rounded shadow hover:bg-white"
                              >
                                <ChevronLeft className="h-4 w-4 text-slate-700" />
                              </button>
                            )}
                            {index < form.images.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveImage(index, index + 1)}
                                className="p-0.5 bg-white/90 rounded shadow hover:bg-white"
                              >
                                <ChevronRight className="h-4 w-4 text-slate-700" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SectionHeader>


            {/* Video Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Vidéo du véhicule
              </h3>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Style vidéo IA</label>
                <select
                  value={form.video_style || 'coastal'}
                  onChange={(e) => setForm(prev => ({ ...prev, video_style: e.target.value }))}
                  className="w-full sm:w-64 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm"
                  data-testid="vehicle-form-video-style-select"
                >
                  {(videoStyles.length ? videoStyles : [{ key: 'coastal', label: 'Côtier' }, { key: 'city', label: 'Ville' }, { key: 'showroom', label: "Salle d'exposition" }]).map(s => (
                    <option key={s.key} value={s.key}>{s.label}{s.configured === false ? ' (vidéo modèle manquante)' : ''}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Utilisé pour la prochaine vidéo générée pour ce véhicule.</p>
              </div>
              {form.video_url ? (
                <div className="space-y-2">
                  <video src={form.video_url} controls className="w-full max-h-48 rounded-lg bg-black" />
                  <span className="inline-block text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">En ligne</span>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, video_url: '' }))}
                    className="text-xs text-red-500 hover:text-red-700 font-medium ml-2"
                  >
                    Supprimer
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                  />
                  <label htmlFor="video-upload" className="cursor-pointer block border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    {uploadingVideo ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-6 w-6 text-blue-500 animate-spin mb-1" />
                        <p className="text-slate-600 text-sm">Téléversement...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <svg className="h-8 w-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        <p className="text-slate-600 text-sm">Cliquez pour ajouter une vidéo</p>
                        <span className="text-xs text-slate-400 mt-1">MP4, MOV, WebM • Max 100MB</span>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>


            {/* Equipment Section - Collapsed by default */}
            <SectionHeader id="equipment" icon={Check} title="Équipements" subtitle={`${form.features.length} équipement(s) sélectionné(s)`} expanded={expandedSections.equipment} onToggle={toggleSection}>
              <div className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[250px] overflow-y-auto pr-2">
                  {EQUIPMENT_OPTIONS.map((feature) => (
                    <div 
                      key={feature} 
                      onClick={() => toggleFeature(feature)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-all text-sm ${
                        form.features.includes(feature) 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                        form.features.includes(feature) ? 'bg-white/20' : 'border border-slate-300'
                      }`}>
                        {form.features.includes(feature) && <Check className="h-3 w-3" />}
                      </div>
                      <span className="truncate">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionHeader>

            {/* Description */}
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${generatingDescription ? 'bg-purple-100' : 'bg-blue-50'}`}>
                    {generatingDescription ? (
                      <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                    ) : (
                      <FileText className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      Description
                      {generatingDescription && (
                        <span className="text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                          IA en cours...
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {autoGenerateEnabled ? '✨ Génération automatique activée' : 'Génération manuelle'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoGenerateEnabled}
                      onChange={(e) => setAutoGenerateEnabled(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Auto
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateDescription}
                    disabled={generatingDescription || !form.make || !form.model}
                    className="bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600 disabled:opacity-50"
                  >
                    {generatingDescription ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Régénérer
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                value={form.description}
                onChange={(e) => {
                  setForm({ ...form, description: e.target.value });
                  // Disable auto-generate if user manually edits
                  if (autoGenerateEnabled) {
                    setAutoGenerateEnabled(false);
                  }
                }}
                placeholder="La description sera générée automatiquement lorsque vous sélectionnez des équipements ou badges CARFAX..."
                rows={6}
                className={`bg-slate-50 border-slate-200 resize-none ${generatingDescription ? 'opacity-50' : ''}`}
              />
              <p className="text-xs text-slate-400 mt-2">
                💡 Sélectionnez des équipements ou badges CARFAX - la description se génère automatiquement. Désactivez "Auto" pour éditer manuellement.
              </p>
            </div>

            {/* Status and Featured Section */}
            <SectionHeader id="status" icon={Star} title="Statut et visibilité" subtitle="Disponibilité et mise en vedette" expanded={expandedSections.status} onToggle={toggleSection}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div>
                  <Label className="text-slate-700 font-medium text-sm mb-2 block">Statut du véhicule</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-white border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500" />
                          Disponible
                        </span>
                      </SelectItem>
                      <SelectItem value="reserved">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-purple-500" />
                          Réservé
                        </span>
                      </SelectItem>
                      <SelectItem value="pending">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500" />
                          En attente
                        </span>
                      </SelectItem>
                      <SelectItem value="sold">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500" />
                          Vendu (masqué)
                        </span>
                      </SelectItem>
                      <SelectItem value="offline">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-slate-500" />
                          Hors-Ligne (masqué)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {(form.status === 'sold' || form.status === 'offline') && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <EyeOff className="h-3 w-3" />
                      Ce véhicule ne sera pas visible sur le site public
                    </p>
                  )}
                </div>
                <div className="flex items-center">
                  <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200 w-full">
                    <Checkbox
                      id="featured"
                      checked={form.is_featured}
                      onCheckedChange={(checked) => setForm({ ...form, is_featured: checked })}
                      className="h-5 w-5 border-amber-400 data-[state=checked]:bg-amber-500"
                    />
                    <div>
                      <Label htmlFor="featured" className="text-slate-700 cursor-pointer font-medium">
                        Mettre en vedette
                      </Label>
                      <p className="text-xs text-slate-500">Afficher sur la page d&apos;accueil</p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionHeader>

            <DialogFooter className="gap-3 pt-6 border-t sticky bottom-0 bg-slate-50 pb-2 -mx-6 px-6 -mb-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="px-6">
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 px-8" disabled={submitting} data-testid="vehicle-form-submit">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  selectedVehicle ? 'Mettre à jour' : 'Ajouter le véhicule'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-800">Supprimer le véhicule</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Êtes-vous sûr de vouloir supprimer {selectedVehicle?.year} {selectedVehicle?.make} {selectedVehicle?.model}? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Window Sticker Modal */}
      {stickerVehicle && (
        <WindowSticker 
          vehicleId={stickerVehicle.id}
          stockNumber={stickerVehicle.stock_number}
          vehicleData={stickerVehicle}
          onClose={() => setStickerVehicle(null)}
          onPrinted={() => markStickersPrinted([stickerVehicle.id])}
        />
      )}

      {/* Bulk Window Sticker Modal */}
      {showBulkSticker && (
        <BulkWindowSticker 
          vehicles={getSelectedVehiclesData()}
          onPrinted={(ids) => markStickersPrinted(ids)}
          onClose={() => {
            setShowBulkSticker(false);
            clearSelection();
          }}
        />
      )}

      <VideoStyleDialog
        request={styleRequest}
        styles={videoStyles}
        onCancel={() => setStyleRequest(null)}
        onConfirm={(style) => { const r = styleRequest; setStyleRequest(null); r.run(style); }}
      />
      <VideoStylesManager
        open={stylesManagerOpen}
        onOpenChange={setStylesManagerOpen}
        styles={videoStyles}
        getAuthHeader={getAuthHeader}
        onChanged={fetchVideoStyles}
      />
      <VideoReviewDialog
        open={!!review}
        onOpenChange={(o) => { if (!o) setReview(null); }}
        vehicleId={review?.vehicleId}
        getAuthHeader={getAuthHeader}
        onChanged={fetchVehicles}
      />
    </AdminLayout>
  );
};

export default AdminVehicles;
